import * as React from 'react';
import Link from '@mui/material/Link';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell, { tableCellClasses } from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Title from './Title';
import Paper from '@mui/material/Paper';
import axios from "axios";
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import Collapse from '@mui/material/Collapse';
import TableContainer from '@mui/material/TableContainer';
import IconButton from '@mui/material/IconButton';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { styled } from '@mui/material/styles';
import { useNavigate } from "react-router-dom";
import LinearProgress from '@mui/material/LinearProgress';
import TextField from '@mui/material/TextField';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import DownloadIcon from '@mui/icons-material/Download';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import Chip from '@mui/material/Chip';

function preventDefault(event) {
    event.preventDefault();
}

// Wspólny StyledTableCell (nagłówki i body)
const StyledTableCell = styled(TableCell)(({ theme }) => ({
    [`&.${tableCellClasses.head}`]: {
        backgroundColor: "#e4e7eb",
        color: theme.palette.common.black,
        fontSize: 12,
        fontWeight: 600
    },
    [`&.${tableCellClasses.body}`]: {
        fontSize: 12,
    },
}));

// Pomocnicze – data do nazwy pliku
function getFileNameDateTime() {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    const hours = String(now.getHours()).padStart(2, '0');
    const minutes = String(now.getMinutes()).padStart(2, '0');
    return `${year}${month}${day}${hours}${minutes}`;
}

// Eksport do CSV – bez zewnętrznych bibliotek
function exportCustomersToCsv(orders) {
    if (!orders || orders.length === 0) return;

    const headers = [
        'Id', 'Type', 'Name', 'FirstName', 'LastName', 'Phone', 'Email', 'IsActive',
        'TaxId', 'State', 'City', 'Address', 'Postcode', 'CreatedAt', 'DBCreatedAt', 'DBUpdatedAt'
    ];

    const rows = orders.map(o => [
        o.Id, o.Type, o.Name, o.Firstname, o.Lastname, o.Phone, o.Email, o.IsActive ? 'Yes' : 'No',
        o.TaxId, o.State, o.City, o.Address, o.Postcode, o.CreatedAt, o.DBCreatedAt, o.DBUpdatedAt
    ]);

    const data = [headers, ...rows];

    const csvString = data
        .map(row =>
            row
                .map(v => (v === null || v === undefined ? '' : String(v)))
                .map(v => v.replace(/"/g, '""'))
                .map(v => `"${v}"`)
                .join(',')
        )
        .join('\r\n');

    const blob = new Blob([csvString], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `klienci_${getFileNameDateTime()}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
}

// Mniejsze Chip z unikalnymi kolorami dla każdego typu [web:122][web:125]
function TypeChip({ type }) {
    if (!type) return null;

    const t = String(type).toLowerCase().trim();
    let colorConfig = {
        backgroundColor: '#e0e0e0',
        color: '#666',
        label: type
    };

    // Unikalne kolory dla różnych typów
    switch (t) {
        case 'academic':
            colorConfig = { backgroundColor: '#1976d2', color: '#fff', label: type };
            break;
        case 'business':
            colorConfig = { backgroundColor: '#2e7d32', color: '#fff', label: type };
            break;
        case 'government':
            colorConfig = { backgroundColor: '#ff9800', color: '#fff', label: type };
            break;
        default:
            // Kolor na podstawie hash wartości dla nowych typów
            const hash = t.split('').reduce((a, b) => {
                a = ((a << 5) - a) + b.charCodeAt(0);
                return a & a;
            }, 0);
            const hue = Math.abs(hash) % 360;
            colorConfig = {
                backgroundColor: `hsl(${hue}, 60%, 45%)`,
                color: '#fff',
                label: type
            };
            break;
    }

    return (
        <Chip
            label={colorConfig.label}
            size="small"
            sx={{
                fontSize: 10, // Jeszcze mniejsza czcionka
                fontWeight: 600,
                height: 18,   // Bardzo mała wysokość
                minWidth: 40, // Minimalna szerokość
                '& .MuiChip-label': {
                    paddingLeft: 0.5,
                    paddingRight: 0.5
                },
                backgroundColor: colorConfig.backgroundColor,
                color: colorConfig.color,
                borderRadius: '10px'
            }}
        />
    );
}

// Ikonka dla IsActive
function ActiveIcon({ isActive }) {
    return isActive ? (
        <CheckCircleIcon sx={{ color: '#2e7d32', fontSize: 18 }} />
    ) : (
        <RemoveCircleOutlineIcon sx={{ color: '#b0b0b0', fontSize: 18 }} />
    );
}

function Row(props) {
    const { order } = props;
    const [open, setOpen] = React.useState(false);

    return (
        <React.Fragment>
            <TableRow sx={{ '& > *': { borderBottom: 'unset' } }}>
                <StyledTableCell>
                    <IconButton
                        aria-label="expand row"
                        size="small"
                        onClick={() => setOpen(!open)}
                    >
                        {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                    </IconButton>
                </StyledTableCell>
                <StyledTableCell sx={{ padding: '4px 8px' }}> {/* Mniejszy padding dla chip */}
                    <TypeChip type={order.Type} />
                </StyledTableCell>
                <StyledTableCell>{order.Name}</StyledTableCell>
                <StyledTableCell>{order.Firstname}</StyledTableCell>
                <StyledTableCell>{order.Lastname}</StyledTableCell>
                <StyledTableCell>{order.Phone}</StyledTableCell>
                <StyledTableCell>{order.Email}</StyledTableCell>
                <StyledTableCell align="center">
                    <ActiveIcon isActive={order.IsActive} />
                </StyledTableCell>
            </TableRow>
            <TableRow>
                <StyledTableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={8}>
                    <Collapse in={open} timeout="auto" unmountOnExit>
                        <Box sx={{ margin: 1 }}>
                            <Typography variant="subtitle2" gutterBottom>
                                Szczegóły klienta
                            </Typography>
                            <Table size="small" aria-label="details">
                                <TableHead>
                                    <TableRow>
                                        <StyledTableCell>CustomerId</StyledTableCell>
                                        <StyledTableCell>TaxId</StyledTableCell>
                                        <StyledTableCell>State</StyledTableCell>
                                        <StyledTableCell>City</StyledTableCell>
                                        <StyledTableCell>Address</StyledTableCell>
                                        <StyledTableCell>Postcode</StyledTableCell>
                                        <StyledTableCell>CreatedAt</StyledTableCell>
                                        <StyledTableCell>DBCreatedAt</StyledTableCell>
                                        <StyledTableCell>DBUpdatedAt</StyledTableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    <TableRow key={order.Id}>
                                        <StyledTableCell component="th" scope="row">{order.Id}</StyledTableCell>
                                        <StyledTableCell>{order.TaxId}</StyledTableCell>
                                        <StyledTableCell>{order.State}</StyledTableCell>
                                        <StyledTableCell>{order.City}</StyledTableCell>
                                        <StyledTableCell>{order.Address}</StyledTableCell>
                                        <StyledTableCell>{order.Postcode}</StyledTableCell>
                                        <StyledTableCell>{order.CreatedAt}</StyledTableCell>
                                        <StyledTableCell>{order.DBCreatedAt}</StyledTableCell>
                                        <StyledTableCell>{order.DBUpdatedAt}</StyledTableCell>
                                    </TableRow>
                                </TableBody>
                            </Table>
                        </Box>
                    </Collapse>
                </StyledTableCell>
            </TableRow>
        </React.Fragment>
    );
}

export default function Customers() {
    const [orders, setOrders] = React.useState([]);
    const [loading, setLoading] = React.useState(true);
    const [search, setSearch] = React.useState('');

    React.useEffect(() => {
        (async () => {
            try {
                const response = await axios.get("db/getcustomerslist/");
                setOrders(response.data || []);
            } catch (error) {
                console.log(error);
                if (error?.response?.status === 401) {
                    alert("Your access is not allowed.");
                }
            } finally {
                setLoading(false);
            }
        })();
    }, []);

    const navigate = useNavigate();
    const routeChange = () => {
        let path = "/orders";
        navigate(path);
    };

    const filteredOrders = React.useMemo(() => {
        if (!search) return orders;
        const q = search.toLowerCase();
        return orders.filter(o => {
            return (
                (o.Name && o.Name.toLowerCase().includes(q)) ||
                (o.Firstname && o.Firstname.toLowerCase().includes(q)) ||
                (o.Lastname && o.Lastname.toLowerCase().includes(q)) ||
                (o.Email && o.Email.toLowerCase().includes(q)) ||
                (o.TaxId && o.TaxId.toLowerCase().includes(q)) ||
                (o.Phone && o.Phone.toLowerCase().includes(q)) ||
                (o.City && o.City.toLowerCase().includes(q))
            );
        });
    }, [orders, search]);

    const renderOrdersTable = (ordersToRender) => {
        return (
            <React.Fragment>
                <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column' }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                        <Title>Klienci</Title>
                        <Stack direction="row" spacing={2} alignItems="center">
                            <TextField
                                size="small"
                                variant="outlined"
                                label="Szukaj klienta"
                                placeholder="Nazwa, NIP, email..."
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                            />
                            <Button
                                variant="outlined"
                                startIcon={<DownloadIcon />}
                                onClick={() => exportCustomersToCsv(orders)}
                            >
                                Eksportuj CSV
                            </Button>
                        </Stack>
                    </Box>

                    <TableContainer>
                        <Table size="small" aria-label="collapsible table" padding="checkbox">
                            <TableHead>
                                <TableRow>
                                    <StyledTableCell />
                                    <StyledTableCell>Type</StyledTableCell>
                                    <StyledTableCell>CustomerName</StyledTableCell>
                                    <StyledTableCell>FirstName</StyledTableCell>
                                    <StyledTableCell>LastName</StyledTableCell>
                                    <StyledTableCell>Phone</StyledTableCell>
                                    <StyledTableCell>Email</StyledTableCell>
                                    <StyledTableCell align="center">IsActive</StyledTableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {ordersToRender.map((order, index) => (
                                    <Row key={index} order={order} />
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    {ordersToRender.length === 0 && (
                        <Box sx={{ mt: 2 }}>
                            <Typography variant="body2" color="text.secondary">
                                Brak klientów spełniających kryteria wyszukiwania.
                            </Typography>
                        </Box>
                    )}
                </Paper>
            </React.Fragment>
        );
    };

    const contents = loading
        ? (
            <Box sx={{ width: '100%' }}>
                <LinearProgress />
                <p><em>&nbsp;&nbsp;Loading... trwa ładowanie danych - stay calm :) ...</em></p>
            </Box>
        )
        : renderOrdersTable(filteredOrders);

    return (
        <>
            {contents}
        </>
    );
}
